// sanity.config.js
import { defineConfig } from "sanity";
import { structureTool } from "sanity/structure";
import { visionTool } from "@sanity/vision";
import schemas from "./schemas/schema";
import { dashboardTool } from "@sanity/dashboard";
import dashboardConfig from "./structure/dashboardConfig";
import { media } from "sanity-plugin-media";
import deskStructure from "./structure/main";
import widenVideo from "./plugins/widen-video";
import CustomStudioLayout from "./components/CustomStudioLayout";
import widenAssetSource from "./plugins/asset-source-widen";
import { CopySelectedFieldsAction } from "./plugins/copy-selected-fields-action/CopySelectedFieldsAction";
import PartialPublishAction from "./plugins/partial-publish-action/PartialPublishAction";
import FixMultilingualAction from "./plugins/fix-multilingual-action/FixMultilingualAction";
import CloneContentAction from "./plugins/clone-content-action/CloneContentAction";
import { SUPPORTED_LANGUAGES } from "./common/localization";
import { internationalizedArray } from "sanity-plugin-internationalized-array";
import languageFilterConfig from "./parts/languageFilterConfig";
import { defaultDocumentNodeResolver } from "./structure/defaultDocumentNodeResolver";
import cruiseDiagnosticDashboardConfig from "./structure/cruiseDiagnosticDashboardConfig";
import "./custom.css";

const dataset = process.env.SANITY_STUDIO_API_DATASET || "dev";

export default defineConfig({
  name: "Silversea",
  title: `Silversea (${dataset})`,
  projectId: "rd0y3pad",
  dataset: dataset,
  plugins: [
    internationalizedArray({
      languages: SUPPORTED_LANGUAGES,
      defaultLanguages: SUPPORTED_LANGUAGES.map((x) => x.id),
      fieldTypes: ["string", "text", "blockContent", "file", "slug"],
      buttonAddAll: true,
      buttonLocations: ["field"],
      languageFilter: languageFilterConfig
    }),
    structureTool({
      structure: deskStructure,
      defaultDocumentNode: defaultDocumentNodeResolver
    }),
    visionTool(),
    media(),
    dashboardTool(dashboardConfig),
    dashboardTool(cruiseDiagnosticDashboardConfig),
    widenVideo()
  ],
  form: {
    image: {
      assetSources: (prev) => [...prev, widenAssetSource],
      directUploads: true
    }
  },
  document: {
    actions: [
      PartialPublishAction,
      CopySelectedFieldsAction,
      FixMultilingualAction,
      CloneContentAction
    ],
    productionUrl: async (prev, context) => {
      // context includes the client and other details
      const { document } = context;
      const maybePath = (document as any)?.metadata?.routing?.path?.en;

      if (maybePath)
        return `https://preview.digital.silversea.com${maybePath}.html`;

      return prev;
    }
  },
  schema: {
    types: schemas,
    templates: (prev) => [
      ...prev,
      {
        id: "shipVersionV2-for-ship",
        title: "Ship Version",
        schemaType: "shipVersionV2",
        parameters: [{ name: "shipId", type: "string" }],
        value: ({ shipId }: { shipId: string }) => ({
          ship: { _type: "reference", _ref: shipId }
        })
      },
      {
        id: "suiteClass-for-group",
        title: "Suite Class",
        schemaType: "suiteClass",
        parameters: [{ name: "suiteClassGroupId", type: "string" }],
        value: ({ suiteClassGroupId }: { suiteClassGroupId: string }) => ({
          suiteClassGroup: {
            _type: "reference",
            _ref: suiteClassGroupId,
            _weak: true,
            _strengthenOnPublish: { type: "suiteClassGroup" }
          }
        })
      }
    ]
  },
  studio: {
    components: {
      layout: CustomStudioLayout
    }
  },
  auth: {
    loginMethod: "token"
  },
  scheduledDrafts: {
    enabled: true
  },
  releases: { enabled: true }
});
